import QRCodeStyling from 'qr-code-styling';

interface QRCodeConfig {
  data: string;
  width?: number;
  height?: number;
  margin?: number;
  image?: string;
  qrOptions?: {
    errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
  };
  imageOptions?: {
    crossOrigin?: string;
    hideBackgroundDots?: boolean;
    imageSize?: number;
    margin?: number;
  };
  dotsOptions?: {
    color?: string;
    type?: string;
    gradient?: any;
  };
  backgroundOptions?: {
    color?: string;
  };
  cornersSquareOptions?: {
    color?: string;
    type?: string;
  };
  cornersDotOptions?: {
    color?: string;
    type?: string;
  };
}

interface QRCodeResult {
  blob: Blob | null;
  error?: string;
}

/**
 * Safely loads an image and converts it to a data URL
 * This helps prevent CORS and file access issues
 */
async function loadImageSafely(imageUrl: string): Promise<string | null> {
  try {
    // If it's already a data URL, return it
    if (imageUrl.startsWith('data:')) {
      return imageUrl;
    }

    // If it's a blob URL, convert it
    if (imageUrl.startsWith('blob:')) {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    }

    // For external URLs, try to load and convert
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      // Add timeout for image loading
      const timeout = setTimeout(() => {
        reject(new Error('Image loading timeout'));
      }, 10000); // 10 second timeout

      img.onload = () => {
        clearTimeout(timeout);
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/png');
          resolve(dataUrl);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('Failed to load image'));
      };

      img.src = imageUrl;
    });
  } catch (error) {
    console.error('Error loading image:', error);
    return null;
  }
}

/**
 * Generates a QR code with robust error handling
 * @param config QR code configuration
 * @returns Promise with blob result and optional error
 */
export async function generateQRCode(config: QRCodeConfig): Promise<QRCodeResult> {
  try {
    let processedConfig = { ...config };

    // Handle image loading if an image is provided
    if (config.image) {
      try {
        const safeImageUrl = await loadImageSafely(config.image);
        if (safeImageUrl) {
          processedConfig.image = safeImageUrl;
        } else {
          // If image fails to load, continue without it
          console.warn('Failed to load QR code logo, generating without image');
          delete processedConfig.image;
        }
      } catch (imageError) {
        console.warn('Error processing QR code image:', imageError);
        // Continue without image
        delete processedConfig.image;
      }
    }

    // Build the QR configuration properly
    const qrStyleConfig: any = {
      width: processedConfig.width || 300,
      height: processedConfig.height || 300,
      margin: processedConfig.margin || 0,
      data: processedConfig.data,
      dotsOptions: processedConfig.dotsOptions || { color: '#000000', type: 'square' },
      backgroundOptions: processedConfig.backgroundOptions || { color: '#FFFFFF' },
      cornersSquareOptions: processedConfig.cornersSquareOptions || { color: '#000000', type: 'square' },
      cornersDotOptions: processedConfig.cornersDotOptions || { color: '#000000', type: 'square' }
    };

    // Add QR options if provided
    if (processedConfig.qrOptions) {
      qrStyleConfig.qrOptions = processedConfig.qrOptions;
    }

    // Only add image and imageOptions if we have a processed image
    if (processedConfig.image) {
      qrStyleConfig.image = processedConfig.image;
      qrStyleConfig.imageOptions = {
        crossOrigin: 'anonymous',
        hideBackgroundDots: true,
        imageSize: 0.4,
        margin: 0,
        ...processedConfig.imageOptions
      };
    }

    // Create QR code instance
    const qr = new QRCodeStyling(qrStyleConfig);

    // Generate blob
    const blob = await qr.getRawData('png');
    
    if (!blob) {
      throw new Error('Failed to generate QR code blob');
    }

    return { blob };
  } catch (error) {
    console.error('Error generating QR code:', error);
    
    // Try to generate a basic QR code without any styling as fallback
    try {
      const fallbackQR = new QRCodeStyling({
        width: config.width || 300,
        height: config.height || 300,
        data: config.data,
        margin: 0,
        dotsOptions: {
          color: '#000000',
          type: 'square'
        },
        backgroundOptions: {
          color: '#FFFFFF'
        }
      });

      const fallbackBlob = await fallbackQR.getRawData('png');
      
      return {
        blob: fallbackBlob,
        error: 'Generated fallback QR code without styling'
      };
    } catch (fallbackError) {
      return {
        blob: null,
        error: `Failed to generate QR code: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }
}

/**
 * Downloads a QR code blob as a file
 * @param blobOrUrl The QR code blob or URL
 * @param filename The filename for download
 */
export async function downloadQRCode(blobOrUrl: Blob | string, filename: string = 'qrcode.png'): Promise<void> {
  let url: string;
  
  if (typeof blobOrUrl === 'string') {
    // If it's a URL, fetch the image
    try {
      const response = await fetch(blobOrUrl);
      const blob = await response.blob();
      url = URL.createObjectURL(blob);
    } catch (error) {
      console.error('Failed to fetch QR code for download:', error);
      // Fallback to direct URL download
      url = blobOrUrl;
    }
  } else {
    // If it's a blob, create object URL
    url = URL.createObjectURL(blobOrUrl);
  }
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Only revoke if we created the URL
  if (typeof blobOrUrl !== 'string' || url !== blobOrUrl) {
    URL.revokeObjectURL(url);
  }
}

/**
 * Converts a QR code blob to base64 data URL
 * @param blob The QR code blob
 * @returns Promise with base64 data URL
 */
export async function qrBlobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}